import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../common/Modal';
import { QrCodeIcon, CameraIcon } from '@heroicons/react/24/outline';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onDetected: (code: string) => void;
  /** folyamatos olvasás (leltár, több tétel) – különben az első találatnál bezár */
  continuous?: boolean;
  title?: string;
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'code_93', 'itf', 'codabar', 'qr_code', 'data_matrix'];
const ZXING_URL = 'https://cdn.jsdelivr.net/npm/@zxing/browser@0.2.1/+esm';

/** Rövid sípszó + rezgés találatkor. */
const feedback = () => {
  try { (navigator as any).vibrate?.(60); } catch { /* ignore */ }
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new AC(); const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = 1320; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination); o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 90);
  } catch { /* ignore */ }
};

/**
 * Vonalkód / QR olvasó telefonkamerával.
 * 1) natív BarcodeDetector (Android Chrome, Edge, macOS) – gyors, telepítés nélkül;
 * 2) tartalék: ZXing futásidőben CDN-ről (iPhone Safari, Firefox);
 * 3) kézi bevitel – ide a USB / Bluetooth kézi olvasók is „begépelnek” (Enter-rel zárva).
 */
const BarcodeScanner: React.FC<Props> = ({ isOpen, onClose, onDetected, continuous = false, title = 'Vonalkód olvasás' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopRef = useRef<() => void>(() => {});
  const lastRef = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  const [status, setStatus] = useState<'idle' | 'starting' | 'scanning' | 'error'>('idle');
  const [engine, setEngine] = useState<'native' | 'zxing' | ''>('');
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [hits, setHits] = useState<string[]>([]);

  const handle = useCallback((raw: string) => {
    const code = String(raw || '').trim();
    if (!code) return;
    const now = Date.now();
    if (code === lastRef.current.code && now - lastRef.current.at < 1500) return; // ugyanaz a kód 1,5 mp-en belül
    lastRef.current = { code, at: now };
    feedback();
    setHits(h => [code, ...h].slice(0, 5));
    onDetected(code);
    if (!continuous) onClose();
  }, [onDetected, continuous, onClose]);

  const stop = useCallback(() => {
    try { stopRef.current(); } catch { /* ignore */ }
    stopRef.current = () => {};
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) { stop(); setStatus('idle'); setHits([]); return; }
    let cancelled = false;
    (async () => {
      setStatus('starting'); setError('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current!;
        video.srcObject = stream;
        await video.play();

        const BD = (window as any).BarcodeDetector;
        let nativeOk = false;
        if (BD) {
          try {
            const supported: string[] = (await BD.getSupportedFormats?.()) || FORMATS;
            const detector = new BD({ formats: FORMATS.filter(f => supported.includes(f)) });
            nativeOk = true;
            setEngine('native');
            let raf = 0; let busy = false; let last = 0;
            const tick = async (t: number) => {
              raf = requestAnimationFrame(tick);
              if (busy || t - last < 180 || video.readyState < 2) return;
              busy = true; last = t;
              try { const res = await detector.detect(video); if (res && res[0]?.rawValue) handle(res[0].rawValue); } catch { /* frame hiba */ }
              busy = false;
            };
            raf = requestAnimationFrame(tick);
            stopRef.current = () => cancelAnimationFrame(raf);
          } catch { nativeOk = false; }
        }
        if (!nativeOk) {
          const mod: any = await import(/* webpackIgnore: true */ ZXING_URL as any);
          if (cancelled) return;
          const reader = new mod.BrowserMultiFormatReader();
          setEngine('zxing');
          const controls = await reader.decodeFromVideoElement(video, (result: any) => { if (result) handle(result.getText()); });
          stopRef.current = () => { try { controls.stop(); } catch { /* ignore */ } };
        }
        setStatus('scanning');
      } catch (e: any) {
        if (cancelled) return;
        setStatus('error');
        setError(e?.name === 'NotAllowedError' ? 'A kamera használata nincs engedélyezve – a böngésző címsoránál engedélyezhető. Addig a kézi bevitel működik.'
          : e?.name === 'NotFoundError' ? 'Nincs elérhető kamera ezen az eszközön. Használd a kézi bevitelt vagy egy USB-s olvasót.'
          : 'A kamera nem indult el. Kézi bevitellel folytathatod.');
      }
    })();
    return () => { cancelled = true; stop(); };
  }, [isOpen, handle, stop]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md" zIndex={70}>
      <div className="space-y-3">
        <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          {status === 'scanning' && <div className="absolute inset-x-8 top-1/2 h-0.5 bg-rose-500/80 shadow-[0_0_8px_rgba(244,63,94,.8)]" />}
          {status !== 'scanning' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white/90 text-sm px-6 gap-2">
              <CameraIcon className="w-8 h-8 opacity-70" />
              {status === 'starting' ? 'Kamera indítása…' : status === 'error' ? error : ''}
            </div>
          )}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400">
          {status === 'scanning' ? `Tartsd a kódot a vonalhoz. ${engine === 'zxing' ? '(kompatibilis mód)' : ''}${continuous ? ' Folyamatos olvasás – minden találat hozzáadódik.' : ''}` : 'Kézi bevitel / USB-s olvasó: írd vagy olvasd be ide, majd Enter.'}
        </div>
        <form onSubmit={e => { e.preventDefault(); handle(manual); setManual(''); }} className="flex gap-2">
          <input value={manual} onChange={e => setManual(e.target.value)} placeholder="Vonalkód vagy cikkszám" autoFocus={status === 'error'} className="flex-1 border rounded-lg px-3 h-10 tabular-nums" inputMode="text" />
          <button type="submit" className="crm-btn crm-btn-secondary h-10"><QrCodeIcon className="w-4 h-4" />OK</button>
        </form>
        {hits.length > 0 && <div className="text-xs text-gray-500 dark:text-gray-400">Utolsó: {hits.join(' · ')}</div>}
        {continuous && <div className="flex justify-end"><button type="button" onClick={onClose} className="crm-btn crm-btn-primary">Kész</button></div>}
      </div>
    </Modal>
  );
};

export default BarcodeScanner;
